import { useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
// components
import Iconify from 'src/components/iconify';
import { MotionContainer, varFade } from 'src/components/animate';
import { m } from 'framer-motion';
import RequestDemo from 'src/components/request-demo';
// utils
import { RESTAURANT_FAQS, SUPPLIER_FAQS } from 'src/utils/data';

// ----------------------------------------------------------------------

export default function FAQPage() {
  const [selectedType, setSelectedType] = useState(null);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
  };

  const getFAQs = () => {
    if (selectedType === 'restaurant') return RESTAURANT_FAQS;
    if (selectedType === 'supplier') return SUPPLIER_FAQS;
    return [];
  };

  return (
    <MotionContainer>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 8 },
          textAlign: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: { xs: 0, md: 4 },
          mx: { xs: 0, md: 3 },
          mb: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          },
        }}
      >
        <m.div variants={varFade().inUp}>
          <Typography variant="h1" sx={{ mb: 4, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', opacity: 0.9, mb: 6 }}>
            Get answers to the most common questions about Cartify
          </Typography>

          {/* Watch Demo Button */}
          <m.div variants={varFade().inUp}>
            <RequestDemo
              buttonText="Watch Demo"
              buttonVariant="outlined"
              buttonSize="large"
              startIcon={<Iconify icon="eva:play-circle-fill" />}
              title="Request a Demo"
              description="Fill out the form below and we'll get back to you within 24 hours to schedule your personalized demo."
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                px: 4,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.3s ease',
              }}
            />
          </m.div>
        </m.div>
      </Box>

      {/* Business Type Selection */}
      <m.div variants={varFade().inUp}>
        <Card
          sx={{
            p: { xs: 4, md: 6 },
            mb: { xs: 6, md: 8 },
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            borderRadius: 4,
          }}
        >
          <Typography variant="h3" sx={{ mb: 4, fontWeight: 600 }}>
            I am a...
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant={selectedType === 'restaurant' ? 'contained' : 'outlined'}
              size="large"
              onClick={() => handleTypeSelect('restaurant')}
              startIcon={<Iconify icon="eva:shopping-bag-fill" />}
              sx={{
                minWidth: 250,
                py: 3,
                px: 6,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 4,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              I&apos;m a Restaurant
            </Button>

            <Button
              variant={selectedType === 'supplier' ? 'contained' : 'outlined'}
              size="large"
              onClick={() => handleTypeSelect('supplier')}
              startIcon={<Iconify icon="eva:shopping-cart-fill" />}
              sx={{
                minWidth: 250,
                py: 3,
                px: 6,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 4,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              I&apos;m a Supplier
            </Button>
          </Stack>
        </Card>
      </m.div>

      {/* FAQ Accordion */}
      {selectedType && (
        <m.div variants={varFade().inUp}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>
                {selectedType === 'restaurant' ? 'Restaurant FAQs' : 'Supplier FAQs'}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>
                Find answers to the most common questions about using Cartify
              </Typography>
            </Box>

            <Stack spacing={3}>
              {getFAQs().map((faq, index) => (
                <Accordion
                  key={index}
                  sx={{
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      bgcolor: 'background.paper',
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                    sx={{
                      px: 4,
                      py: 3,
                      '& .MuiAccordionSummary-content': {
                        my: 1,
                      },
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 4, pb: 4 }}>
                    <Typography
                      variant="body1"
                      sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '1rem' }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Container>
        </m.div>
      )}

      {/* Empty State */}
      {!selectedType && (
        <m.div variants={varFade().inUp}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', py: { xs: 6, md: 8 } }}>
              <Iconify
                icon="eva:question-mark-circle-fill"
                sx={{ fontSize: 80, color: 'text.disabled', mb: 4 }}
              />
              <Typography variant="h5" sx={{ mb: 3, color: 'text.secondary' }}>
                Choose your business type above
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.disabled' }}>
                Select whether you&apos;re a restaurant or supplier to see relevant frequently asked
                questions
              </Typography>
            </Box>
          </Container>
        </m.div>
      )}

      {/* Bottom Spacing */}
      <Box sx={{ pb: { xs: 6, md: 8 } }} />
    </MotionContainer>
  );
}
